import { MessageOutlined, UserOutlined } from "@ant-design/icons";
import type { SeatableReservation } from "../../../../../api/reservations";
import { formatClock } from "../../../../reservations/logic/dayTime";
import { reservationEndMs, reservationStartMs } from "../../../../reservations/logic/tableStatus";
import { seatTimingOf, timingToneClass } from "./seatReservationGroups";
import { useSeatFormat } from "./useSeatFormat";

/**
 * Ред от списъка на „Настани": час · име · маси · гости · „−36 мин"
 * / „след 9 мин". Закъснелият е с червена лента вляво, избраният — с accent
 * рамка и се разгъва на място: телефон | зала | часове | бележка (на един
 * ред, бележката се реже). Разгънатото започва под името (същият ляв ръб)
 * на всяка ширина; защо не може да се настани пише бутонът долу. На телефон
 * основният ред е на два реда, за да не се реже името.
 */
export const SeatReservationRow = ({
  reservation,
  nowMs,
  selected,
  isNarrow,
  onClick,
}: {
  reservation: SeatableReservation;
  nowMs: number;
  selected: boolean;
  isNarrow: boolean;
  onClick: () => void;
}) => {
  const format = useSeatFormat();
  const timing = seatTimingOf(reservation, nowMs);
  const isLate = timing.kind === "late";
  const regions = [...new Set(reservation.regionNames)].join(", ");

  const clock = (
    <span
      className={`w-[46px] shrink-0 font-pos-mono text-[15px] font-bold leading-[1.2] ${
        isLate ? "text-pos-danger-ink" : "text-pos-ink"
      }`}
    >
      {formatClock(reservationStartMs(reservation))}
    </span>
  );
  const name = (
    <span className="min-w-0 flex-1 truncate text-[14px] font-bold leading-[1.2] text-pos-ink">
      {reservation.guestName}
    </span>
  );
  const tables = (
    <span className="shrink-0 whitespace-nowrap rounded-[6px] bg-pos-addon-bg px-2 py-0.5 text-[12px] font-bold text-pos-addon-ink">
      {format.tables(reservation.tableNames)}
    </span>
  );
  const guests = (
    <span className="inline-flex shrink-0 items-center gap-1 text-[12.5px] font-medium text-pos-soft2">
      <UserOutlined className="text-[11px]" />
      {reservation.guests}
    </span>
  );
  const phone = <span className="shrink-0 font-pos-mono font-semibold text-pos-ink2">{reservation.phone}</span>;
  const status = (
    <span className={`shrink-0 whitespace-nowrap text-right text-[12.5px] font-bold ${timingToneClass(timing)}`}>
      {format.timingShort(timing)}
    </span>
  );

  return (
    <button
      type="button"
      data-seat-row={reservation.id}
      onClick={onClick}
      aria-pressed={selected}
      // scroll-mt: при ↑/↓ редът спира под залепения разделител на часа.
      className={`relative flex w-full scroll-mt-9 scroll-mb-2 cursor-pointer flex-col gap-1.5 overflow-hidden rounded-[10px] border px-3.5 text-left transition-colors ${
        isNarrow ? "py-2.5" : "min-h-[46px] justify-center py-2"
      } ${selected ? "border-pos-accent bg-pos-marked" : "border-pos-line3 bg-pos-card hover:bg-pos-canvas"}`}
    >
      {isLate && <span className="absolute inset-y-0 left-0 w-[3px] bg-pos-danger-ink" />}
      {isNarrow ? (
        <>
          <span className="flex w-full min-w-0 items-center gap-3">
            {clock}
            {name}
            {status}
          </span>
          <span className="flex min-w-0 items-center gap-3 pl-[58px]">
            {tables}
            {guests}
            {/* Избраният: телефонът — на този ред, след гостите. */}
            {selected && <span className="shrink-0 text-[12.5px]">{phone}</span>}
          </span>
        </>
      ) : (
        <span className="flex w-full min-w-0 items-center gap-3">
          {clock}
          {name}
          {tables}
          <span className="w-8">{guests}</span>
          <span className="w-[112px] text-right">{status}</span>
        </span>
      )}

      {selected && (
        // Компютър/таблет: под името — телефон | зала | часове | бележка.
        // Телефон: телефонът е горе (до гостите), тук от ръба на часа —
        // зала | часове | бележка. Бележката винаги се реже с „…".
        <span
          className={`flex min-w-0 items-center gap-2 whitespace-nowrap text-[12.5px] leading-[1.4] text-pos-ink2 ${
            isNarrow ? "" : "pl-[58px]"
          }`}
        >
          {!isNarrow && phone}
          {!isNarrow && <Divider />}
          {regions && <span className="min-w-0 shrink truncate">{regions}</span>}
          {regions && <Divider />}
          <span className="shrink-0 font-pos-mono">
            {formatClock(reservationStartMs(reservation))}–{formatClock(reservationEndMs(reservation))}
          </span>
          {reservation.note && <Divider />}
          {reservation.note && <Note note={reservation.note} />}
        </span>
      )}
    </button>
  );
};

const Note = ({ note }: { note: string }) => (
  <span className="flex min-w-0 items-center gap-1.5 text-pos-accent-ink" title={note}>
    <MessageOutlined className="shrink-0 text-[11px]" />
    <span className="min-w-0 truncate italic">{note}</span>
  </span>
);

const Divider = () => <span className="h-3 w-px shrink-0 bg-pos-line3" />;
